import i18n from '@/i18n';

import { buildHealthCheckDisplayDescriptorsFromForm } from '../../../capabilities/health-check/buildHealthCheckDisplayDescriptors';
import { readHealthCheckFromFormValues } from '../../../capabilities/health-check/healthCheckForm';
import { MAAS_INFERENCE_SOURCE_FIELD } from '../../../capabilities/maas-init';
import { stripBillingOnlyFieldsFromMaasAnnotations } from '../../../capabilities/maas-init/utils/annotations';
import { resolveCommonEnvFromValues } from '../../../capabilities/maas-init/utils/commonEnv';
import { getVolumeStorageClassInfo } from '../../../capabilities/maas-model-storage/storageVolume';
import { formatMaasRunMode, formatMaasRuntimeType } from '../../../capabilities/maas-node-groups/labels';
import { MaasDataDiskMode, getDataDiskMode } from '../../../capabilities/maas-node-groups/masterGroupForm';
import { resolveUsageScenarioLabel } from '../../../capabilities/service/constants';
import {
  SERVICE_MOUNT_GROUP_NAME_FIELD,
  formatServiceMountGroupNamesDisplay,
  normalizeServiceMountGroupNames,
} from '../../../capabilities/service/serviceMountNode';
import { normalizeServiceList } from '../../../capabilities/service/utils';
import type { PipelineContext } from '../../../core/types';
import type { DisplayDescriptor } from '../../../platform/display-descriptors';
import type { ResourceType } from '../../../platform/pipeline-kernel/types';
import {
  appendAggregatedMaasNodeComputeDisplayRows,
  appendAggregatedStorageVolumeDisplayRow,
  appendMaasNodeComputeResourceConfigs,
  appendPerNodeSystemVolumeDisplayRows,
  appendStorageVolumeResourceConfigs,
  appendSystemVolumeResourceConfigs,
  parseMaasNodeGroups,
  toResourceConfig,
} from '../../_shared/workload-agg/displayHelpers';
import { buildAppInstanceImagePreviewRows } from '../display/resolveAppImageSummary';
import { computeTemplates, dataVolume, systemDiskSize } from '../resourceConfigs';
import type { ApplicationInstanceFormValues } from '../types';
import { parseAppStorageGroupsFromValues } from './appStorageGroups';
import { isAppInstanceModuleEnabled } from './enabledModules';
import type { AppInstanceApiPayload, AppInstanceSpec } from './schema';
import { appInstanceToForm } from './spec';

const t = i18n.t.bind(i18n);

function pushDisplayRow(arr: DisplayDescriptor[], item: DisplayDescriptor) {
  if (!item.label || !item.value) return;
  arr.push(item);
}

function getMaasComputeDisplayLabels() {
  return {
    cpu: 'CPU',
    memory: t('common:memory'),
    gpu: 'GPU',
  };
}

function specToFormValues(spec: AppInstanceSpec): ApplicationInstanceFormValues {
  return appInstanceToForm(spec) as ApplicationInstanceFormValues;
}

/** 直建 / 下单 apiBody：剥离询价专用 annotations 字段，避免污染提交体 */
export function serializeAppInstanceApiBody(spec: AppInstanceSpec, _ctx: PipelineContext): AppInstanceApiPayload {
  const annotations = stripBillingOnlyFieldsFromMaasAnnotations(spec.annotations);
  return {
    ...spec,
    ...(annotations ? { annotations } : { annotations: undefined }),
  };
}

/**
 * SKU preset 投影（测试 / 非直序列化路径）。
 * 生产 prepareSkuSubmit 对已注册插件走表单直序列化；AppInstance 注册前仍用 pages adapter。
 */
export function pickAppInstancePresetFields(spec: AppInstanceSpec, _ctx: PipelineContext): Record<string, unknown> {
  return { ...spec };
}

/** 合并 hydrate 表单与可选 UI 现值（镜像卡片元数据常仅在 form 侧） */
function resolveProjectionFormValues(spec: AppInstanceSpec, ctx: PipelineContext): ApplicationInstanceFormValues {
  const hydrated = specToFormValues(spec);
  if (!ctx.formValues) return hydrated;
  const form = ctx.formValues;
  return {
    ...hydrated,
    ...form,
    // undefined 叶子不覆盖 hydrate（form→spec 往返会丢厂商/架构等）
    [MAAS_INFERENCE_SOURCE_FIELD]: form[MAAS_INFERENCE_SOURCE_FIELD] ?? hydrated.maasInferenceSource,
    _inferenceSystem: form._inferenceSystem ?? hydrated._inferenceSystem,
    inferenceFrameworkImage: form.inferenceFrameworkImage ?? hydrated.inferenceFrameworkImage,
  } as ApplicationInstanceFormValues;
}

function appendAppServiceImageDisplayRows(arr: DisplayDescriptor[], values: Record<string, unknown>) {
  buildAppInstanceImagePreviewRows(values, t).forEach((row) => {
    pushDisplayRow(arr, { ...row, group: 'appService' });
  });
}

/**
 * Display 投影入口。
 * 默认路径供 SKU `displayConfig` 等持久化；仅 `previewModuleLayout: 'order-fold'` 按勾选块补齐 runtime/service/healthCheck。
 */
export function buildAppInstanceDisplayDescriptors(spec: AppInstanceSpec, ctx: PipelineContext): DisplayDescriptor[] {
  if (ctx.previewModuleLayout === 'order-fold') {
    return buildAppInstanceOrderFoldDisplayDescriptors(spec, ctx);
  }
  return buildAppInstancePersistedDisplayDescriptors(spec, ctx);
}

/** SKU displayConfig 等：保持既有 group（appService / envVars / nodeGroups / storage） */
function buildAppInstancePersistedDisplayDescriptors(spec: AppInstanceSpec, ctx: PipelineContext): DisplayDescriptor[] {
  return buildAppInstanceCoreDisplayDescriptors(spec, ctx, { orderFold: false });
}

/** 下单 / 确认预览：补齐运行配置、探活、服务配置分组 */
function buildAppInstanceOrderFoldDisplayDescriptors(spec: AppInstanceSpec, ctx: PipelineContext): DisplayDescriptor[] {
  return buildAppInstanceCoreDisplayDescriptors(spec, ctx, { orderFold: true });
}

function buildAppInstanceCoreDisplayDescriptors(
  spec: AppInstanceSpec,
  ctx: PipelineContext,
  options: { orderFold: boolean },
): DisplayDescriptor[] {
  const values = resolveProjectionFormValues(spec, ctx);
  const arr: DisplayDescriptor[] = [];
  const nodes = parseMaasNodeGroups(values);
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const includeRuntimeConfig = options.orderFold && isAppInstanceModuleEnabled('runtimeConfig', enabledModules, skuType);
  const includeAppService = isAppInstanceModuleEnabled('appService', enabledModules, skuType);
  const includeEnvVars = isAppInstanceModuleEnabled('envVars', enabledModules, skuType);
  const includeNodeGroups = isAppInstanceModuleEnabled('nodeGroups', enabledModules, skuType);
  const includeHealthCheck = options.orderFold && isAppInstanceModuleEnabled('healthCheck', enabledModules, skuType);
  const includeService = options.orderFold && isAppInstanceModuleEnabled('service', enabledModules, skuType);
  const includeStorage = isAppInstanceModuleEnabled('storage', enabledModules, skuType);
  const computeLabels = getMaasComputeDisplayLabels();
  const resolvedCommonEnv = resolveCommonEnvFromValues(values);

  if (includeRuntimeConfig) {
    const runtimeTypeLabel = formatMaasRuntimeType(values.runtime?.type, t);
    if (runtimeTypeLabel !== '-') {
      pushDisplayRow(arr, {
        id: 'runtime_type',
        group: 'runtimeConfig',
        label: t('data-center:maas.runtime.type'),
        value: runtimeTypeLabel,
      });
    }
  }

  if (includeAppService) {
    appendAppServiceImageDisplayRows(arr, values as Record<string, unknown>);
  }

  if (includeEnvVars && resolvedCommonEnv?.length) {
    resolvedCommonEnv.forEach((item, index) => {
      pushDisplayRow(arr, {
        id: `common_env_${index}`,
        group: 'envVars',
        label: index === 0 ? t('data-center:maas.common.env.vars') : '',
        value: `${item.name}=${item.value ?? ''}`,
      });
    });
  }

  if (includeNodeGroups) {
    const deployModeLabel = formatMaasRunMode(values.runMode, t);
    if (deployModeLabel !== '-') {
      pushDisplayRow(arr, {
        id: 'deploy_mode',
        group: 'nodeGroups',
        label: t('data-center:maas.deploy.mode'),
        value: deployModeLabel,
      });
    }

    appendAggregatedMaasNodeComputeDisplayRows(
      (item) => pushDisplayRow(arr, { ...item, group: 'nodeGroups' }),
      nodes,
      computeTemplates,
      computeLabels,
      { instanceReplicas: Number(values.replicas) },
    );
    appendPerNodeSystemVolumeDisplayRows(values, nodes, (item) => pushDisplayRow(arr, { ...item, group: 'nodeGroups' }), t);
  }

  if (includeHealthCheck) {
    buildHealthCheckDisplayDescriptorsFromForm(readHealthCheckFromFormValues(values as Record<string, unknown>)).forEach((row) => {
      pushDisplayRow(arr, row);
    });
  }

  if (includeService) {
    const services = normalizeServiceList(values._inferenceService ?? spec.network?.service);
    services.forEach((svc, index) => {
      const name = String(svc.name || '').trim() || t('data-center:maas.section.service');
      const scenario = resolveUsageScenarioLabel(svc.usageScenario);
      const internalPort = svc.internalPort != null ? String(svc.internalPort) : '-';
      const path = String(svc.routerPath || '').trim() || '-';
      pushDisplayRow(arr, {
        id: `service_${index}`,
        group: 'service',
        label: name,
        value: `${scenario} / ${t('common:internal.port')} ${internalPort} / ${t('common:path')} ${path}`,
      });
    });
    const mountNames = normalizeServiceMountGroupNames((values as Record<string, unknown>)[SERVICE_MOUNT_GROUP_NAME_FIELD]);
    const mountDisplay = formatServiceMountGroupNamesDisplay(mountNames, '');
    if (mountDisplay) {
      pushDisplayRow(arr, {
        id: 'service_mount_nodes',
        group: 'service',
        label: t('data-center:maas.service.entry.nodes'),
        value: mountDisplay,
      });
    }
  }

  if (includeStorage) {
    parseAppStorageGroupsFromValues(values).forEach((group, index) => {
      const dataDiskMode = getDataDiskMode(group.volume);
      if (dataDiskMode !== MaasDataDiskMode.Create) return;
      const storageClassInfo = getVolumeStorageClassInfo(group.volume);
      if (!storageClassInfo) return;
      pushDisplayRow(arr, {
        id: `app_storage_class_${index}`,
        group: 'storage',
        label: t('common:storage.class'),
        value: storageClassInfo.alias || storageClassInfo.name || '-',
        storageClassInfo,
      });
    });
    appendAggregatedStorageVolumeDisplayRow(values, (item) => pushDisplayRow(arr, { ...item, group: 'storage' }), dataVolume.unit || 'Gi');
  }

  return arr;
}

/** resourceConfigs 投影：算力 = 节点组副本 × 外部容器副本；系统盘/共享卷不乘 */
export function buildAppInstanceResourceConfigs(spec: AppInstanceSpec, ctx: PipelineContext): ResourceType[] {
  const values = specToFormValues(spec);
  const nodes = parseMaasNodeGroups(values);
  const result: ResourceType[] = [];
  const enabledModules = ctx.sku?.enabledModules;
  const skuType = ctx.sku?.skuType;
  const includeNodeGroups = isAppInstanceModuleEnabled('nodeGroups', enabledModules, skuType);
  const includeStorage = isAppInstanceModuleEnabled('storage', enabledModules, skuType);

  if (includeNodeGroups) {
    appendMaasNodeComputeResourceConfigs(result, nodes, computeTemplates, toResourceConfig, {
      instanceReplicas: Number(values.replicas),
    });
    appendSystemVolumeResourceConfigs(result, systemDiskSize, values, nodes, toResourceConfig);
  }

  if (includeStorage) {
    appendStorageVolumeResourceConfigs(result, dataVolume, values, toResourceConfig);
  }

  return result;
}
